import React, { useState, useEffect } from 'react';
import API_BASE_URL from './config';
import { WORK_TYPES } from './constants';
import './CompanySettings.css';

function CompanySettings({ currentUser }) {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [formData, setFormData] = useState({
        display_name: '',
        address: '',
        tagline: '',
        types_of_work: [],
        logo_url: null
    });
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);

    // Fetch company settings on mount
    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/companies/settings`, {
                credentials: 'include'
            });
            
            if (!response.ok) {
                throw new Error('Failed to fetch company settings');
            }
            
            const data = await response.json();
            setSettings(data);
            setFormData({
                display_name: data.display_name || '',
                address: data.address || '',
                tagline: data.tagline || '',
                types_of_work: data.types_of_work || [],
                logo_url: data.logo_url
            });
            setLoading(false);
        } catch (err) {
            console.error('Error fetching company settings:', err);
            setError(err.message || 'Failed to load company settings');
            setLoading(false);
        }
    };

    const handleLogoUpload = async (file) => {
        // Validate file type
        const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
        if (!allowedTypes.includes(file.type)) {
            setError('Only PNG, JPEG, and SVG images are allowed');
            return;
        }

        // Validate file size (5MB)
        if (file.size > 5 * 1024 * 1024) {
            setError('File size must be less than 5MB');
            return;
        }

        setUploadingLogo(true);
        setError(null);

        try {
            const formDataObj = new FormData();
            formDataObj.append('file', file);

            const response = await fetch(`${API_BASE_URL}/api/onboarding/upload-logo`, {
                method: 'POST',
                credentials: 'include',
                body: formDataObj
            });

            if (!response.ok) {
                throw new Error('Failed to upload logo');
            }

            const data = await response.json();
            setFormData({ ...formData, logo_url: data.logo_url });
        } catch (err) {
            setError(err.message || 'Failed to upload logo');
        } finally {
            setUploadingLogo(false);
        }
    };

    const handleLogoDelete = () => {
        setFormData({ ...formData, logo_url: null });
    };

    const handleWorkTypeToggle = (workType) => {
        const currentTypes = formData.types_of_work || [];
        if (currentTypes.includes(workType)) {
            setFormData({
                ...formData,
                types_of_work: currentTypes.filter(t => t !== workType)
            });
        } else {
            setFormData({
                ...formData,
                types_of_work: [...currentTypes, workType]
            });
        }
    };

    const handleSave = async () => {
        setError(null);
        setMessage(null);

        if (!formData.display_name) {
            setError('Company name is required');
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/api/companies/settings`, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.detail || 'Failed to update settings');
            }

            const data = await response.json();
            setSettings(data.settings);
            setFormData({
                display_name: data.settings.display_name || formData.display_name,
                address: data.settings.address || '',
                tagline: data.settings.tagline || '',
                types_of_work: data.settings.types_of_work || [],
                logo_url: data.settings.logo_url
            });
            setMessage('✅ Company settings updated successfully! Refreshing...');
            setEditing(false);
            
            // Navigate to home to force full refresh (avoids /users route conflict with API)
            setTimeout(() => {
                window.location.href = '/';
            }, 1000);
        } catch (err) {
            setError(err.message || 'Failed to update settings');
        }
    };

    const handleCancel = () => {
        // Reset form to current settings
        setFormData({
            display_name: settings.display_name || '',
            address: settings.address || '',
            tagline: settings.tagline || '',
            types_of_work: settings.types_of_work || [],
            logo_url: settings.logo_url
        });
        setEditing(false);
        setError(null);
        setMessage(null);
    };

    if (loading) {
        return (
            <div className="company-settings">
                <div className="loading-spinner"></div>
                <p>Loading company settings...</p>
            </div>
        );
    }

    return (
        <div className="company-settings">
            <div className="settings-header">
                <h2>Company Settings</h2>
                {!editing && (
                    <button onClick={() => setEditing(true)} className="btn-edit">
                        Edit Settings
                    </button>
                )}
            </div>

            {error && <div className="alert alert-danger">{error}</div>}
            {message && <div className="alert alert-success">{message}</div>}

            {!editing ? (
                // View Mode
                <div className="settings-view">
                    <div className="settings-row">
                        <label>Company Name:</label>
                        <span>{settings.display_name}</span>
                    </div>
                    <div className="settings-row">
                        <label>Address:</label>
                        <span>{settings.address || 'Not set'}</span>
                    </div>
                    <div className="settings-row">
                        <label>Tagline:</label>
                        <span>{settings.tagline || 'Not set'}</span>
                    </div>
                    <div className="settings-row">
                        <label>Types of Work:</label>
                        <span>{settings.types_of_work && settings.types_of_work.length > 0 
                            ? settings.types_of_work.join(', ') 
                            : 'Not set'}</span>
                    </div>
                    <div className="settings-row">
                        <label>Logo:</label>
                        <div>
                            {settings.logo_url ? (
                                <img 
                                    src={`${API_BASE_URL}${settings.logo_url}`} 
                                    alt="Company logo"
                                    className="logo-preview"
                                    onError={(e) => {
                                        console.error('Logo failed to load:', settings.logo_url);
                                        e.target.style.display = 'none';
                                    }}
                                />
                            ) : (
                                <span className="text-muted">No logo uploaded</span>
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                // Edit Mode
                <div className="settings-edit">
                    <div className="form-group">
                        <label>Company Name *</label>
                        <input
                            type="text"
                            value={formData.display_name}
                            onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                            className="form-control"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Address</label>
                        <input
                            type="text"
                            value={formData.address}
                            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                            className="form-control"
                            placeholder="123 Main St, City, State ZIP"
                        />
                    </div>

                    <div className="form-group">
                        <label>Tagline</label>
                        <input
                            type="text"
                            value={formData.tagline}
                            onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                            className="form-control"
                            placeholder="Your company's tagline"
                        />
                    </div>

                    <div className="form-group">
                        <label>Types of Work</label>
                        <div className="work-types">
                            {WORK_TYPES.filter(type => type !== 'Unknown').map(type => (
                                <label key={type} className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={(formData.types_of_work || []).includes(type)}
                                        onChange={() => handleWorkTypeToggle(type)}
                                    />
                                    {type}
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Company Logo</label>
                        {formData.logo_url ? (
                            <div className="logo-edit">
                                <img 
                                    src={`${API_BASE_URL}${formData.logo_url}`} 
                                    alt="Company logo"
                                    className="logo-preview"
                                    onError={(e) => {
                                        console.error('Logo failed to load:', formData.logo_url);
                                        e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="150"%3E%3Crect width="200" height="150" fill="%23f0f0f0"/%3E%3Ctext x="100" y="75" text-anchor="middle" dy=".3em" font-family="Arial" font-size="14" fill="%23999"%3ELogo Preview%3C/text%3E%3C/svg%3E';
                                    }}
                                />
                                <div className="logo-actions">
                                    <button 
                                        onClick={() => document.getElementById('logo-replace').click()}
                                        className="btn btn-secondary"
                                        disabled={uploadingLogo}
                                    >
                                        {uploadingLogo ? 'Uploading...' : 'Replace Logo'}
                                    </button>
                                    <button 
                                        onClick={handleLogoDelete}
                                        className="btn btn-danger"
                                        disabled={uploadingLogo}
                                    >
                                        Delete Logo
                                    </button>
                                    <input 
                                        id="logo-replace" 
                                        type="file" 
                                        accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                                        style={{ display: 'none' }}
                                        onChange={(e) => e.target.files[0] && handleLogoUpload(e.target.files[0])}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="logo-upload">
                                <p className="text-muted">No logo uploaded</p>
                                <button 
                                    onClick={() => document.getElementById('logo-upload').click()}
                                    className="btn btn-primary"
                                    disabled={uploadingLogo}
                                >
                                    {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                                </button>
                                <input 
                                    id="logo-upload" 
                                    type="file" 
                                    accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                                    style={{ display: 'none' }}
                                    onChange={(e) => e.target.files[0] && handleLogoUpload(e.target.files[0])}
                                />
                                <p className="text-muted" style={{ fontSize: '12px', marginTop: '10px' }}>
                                    PNG, JPEG, or SVG, max 5MB
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="settings-footer">
                        <button onClick={handleCancel} className="btn btn-secondary">
                            Cancel
                        </button>
                        <button onClick={handleSave} className="btn btn-primary" disabled={uploadingLogo}>
                            Save Changes
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CompanySettings;

